/**
 * Minimal dependency-free toast.
 *
 * Imperative API — call `showToast('message', 'success' | 'error' | 'info')`
 * from anywhere; the helper attaches a fixed-position container to <body>
 * on first use and stacks multiple toasts vertically. Each toast auto-fades
 * after ~5s; clicking dismisses early.
 *
 * Why not pull in react-hot-toast / sonner? They each add ~5-15 kB and a
 * Provider you have to wire into App.tsx. For our one notification site
 * (archive-link copy fallback) that's all overkill.
 */

const STYLES = {
  info:    { bg: '#1e3a5f', fg: '#ffffff' }, // matches header
  success: { bg: '#065f46', fg: '#d1fae5' },
  error:   { bg: '#991b1b', fg: '#fee2e2' },
};

let host: HTMLDivElement | null = null;

function ensureHost(): HTMLDivElement {
  if (host && document.body.contains(host)) return host;
  host = document.createElement('div');
  host.style.cssText = [
    'position:fixed',
    'top:24px',
    'right:24px',
    'z-index:99999',
    'display:flex',
    'flex-direction:column',
    'gap:8px',
    'pointer-events:none', // children re-enable
    'max-width:380px',
  ].join(';');
  document.body.appendChild(host);
  return host;
}

export function showToast(
  message: string,
  variant: 'info' | 'success' | 'error' = 'info',
  durationMs = 5000,
) {
  const root = ensureHost();
  const style = STYLES[variant];

  const el = document.createElement('div');
  el.style.cssText = [
    `background:${style.bg}`,
    `color:${style.fg}`,
    'padding:12px 16px',
    'border-radius:8px',
    'box-shadow:0 4px 12px rgba(0,0,0,0.15)',
    'font-size:14px',
    'line-height:1.4',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'pointer-events:auto',
    'cursor:pointer',
    'opacity:0',
    'transform:translateX(20px)',
    'transition:opacity 200ms ease, transform 200ms ease',
    'white-space:pre-line',
    'word-break:break-word',
  ].join(';');
  el.textContent = message;
  root.appendChild(el);

  // Trigger fade-in on next frame
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateX(0)';
  });

  const dismiss = () => {
    el.style.opacity = '0';
    el.style.transform = 'translateX(20px)';
    setTimeout(() => el.remove(), 220);
  };
  el.addEventListener('click', dismiss);
  setTimeout(dismiss, durationMs);
}
